import React, { useContext, useEffect, useState } from 'react';
import { copyFor, routes } from 'config';
import { useUser } from 'hooks/use-user.hook';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { Page } from 'styles';
import { BackLink } from 'components/back-link';
import { useMutation } from '@apollo/client';
import { ErrorPage } from 'components/errors';
import { useAsyncSetState } from 'hooks/use-async-set-state.hook';
import { Button, Text, Notification } from '@perch-labs/shrike';
import { SwitchRow } from 'components/switch-row';
import { useAsyncFn } from 'react-use';
import { NOTIFICATION_DISPLAY_TIME } from 'domain/ux';
import { LoadingPage } from 'components/loading';
import { UPDATE_USER_MUTATION } from '../personal-information.api';
import {
  modifyCommunicationPreferences,
  determineSmsOptIn,
  shouldAllowUpdate,
} from './sms-preference.utils';

const getCopy = copyFor('settings.smsPreference');
const { personalInformation } = routes();

export const SmsPreference = () => {
  const { data: user, loading: userLoading, error: userError } = useUser();
  const [userSelectedOptIn, setUserOptIn] = useState(
    determineSmsOptIn(user, 'marketing_sms') || false
  );
  const [userPreferences, setUserPreferences] = useState<(string | null)[]>([]);
  useEffect(() => {
    if (user?.communicationPreferences && !userLoading) {
      setUserPreferences(user.communicationPreferences);
    }
  }, [user?.communicationPreferences, userLoading]);
  const { addNotification } = useContext(Notification.Context);
  const [variables] = useAsyncSetState(user, (stateUser: typeof user) => ({
    userId: stateUser?.id,
  }));
  const [mutate] = useMutation(UPDATE_USER_MUTATION);

  // @ts-expect-error ts-migrate(2339) FIXME: Property 'userId' does not exist on type '{}... Remove this comment to see the full error message
  const { userId } = variables;
  const [{ loading: updateLoading }, handleSubmit] = useAsyncFn(async () => {
    const input = {
      communicationPreferences: modifyCommunicationPreferences(
        userPreferences,
        userSelectedOptIn
      ),
    };

    const { data } = await mutate({
      variables: {
        id: user?.id || userId,
        input,
      },
    });

    setUserPreferences(data.updateUser?.communicationPreferences);

    addNotification({
      displayTime: NOTIFICATION_DISPLAY_TIME,
      message: getCopy('marketing.updateNotification'),
      variant: 'success',
    });
  }, [
    mutate,
    userSelectedOptIn,
    userPreferences,
    modifyCommunicationPreferences,
  ]);

  if (userLoading) return <LoadingPage numberOfSections={2} />;
  if (userError || !user) {
    return <ErrorPage title={getCopy('title')} to={personalInformation} />;
  }

  const toggleSmsOptIn = event => {
    setUserOptIn(event);
  };

  return (
    <>
      <BackLink to={personalInformation} />
      <Page maxWidth={MAX_SECONDARY_PAGE_WIDTH}>
        <Text textStyle="heading800" tag="h1" margin={{ bottom: '48px' }}>
          {getCopy('title')}
        </Text>
        <SwitchRow
          hideTopBorder={true}
          header={getCopy('marketing.header')}
          subHeader={getCopy('marketing.subHeader')}
          toggle={userSelectedOptIn}
          onChange={toggleSmsOptIn}
        />
        <Button
          disabled={shouldAllowUpdate(userPreferences, userSelectedOptIn)}
          fullWidth
          loading={updateLoading}
          margin={{ top: '32px' }}
          onClick={handleSubmit}
          data-testid="update-sms-preference-button"
        >
          {getCopy('updateButton')}
        </Button>
      </Page>
    </>
  );
};
